import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ImagePlus,
  Languages,
  ListPlus,
  Loader2,
  Plus,
  Ruler,
  Save,
  Trash2,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  categoriesApi,
  getApiValidationErrors,
  menusApi,
  productsApi,
} from '../../api';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useConfirm } from '../../components/feedback/ConfirmProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import getTranslatedField from '../../i18n/helpers/getTranslatedField';
import translateApiError from '../../utils/translateApiError';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import '../../styles/Products/product-form.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const MAX_MEDIA_FILES = 30;
const MAX_IMAGE_SIZE_MB = 8;
const MAX_VIDEO_SIZE_MB = 100;

const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-m4v',
];

const isAcceptedMediaFile = (file) => {
  const type = String(file?.type || '').toLowerCase();

  return (
    type.startsWith('image/') ||
    ACCEPTED_VIDEO_TYPES.includes(type)
  );
};

const SUPPORTED_LOCALES = ['fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja', 'it', 'pt', 'tr'];

// A menu controls which translation editors are visible. Existing translations
// for other languages remain in state and are not discarded on save.
// Until the menu API exposes a selection, all 12 editors remain available.
const getMenuLocales = (menu) => {
  if (!menu) return SUPPORTED_LOCALES;

  const raw = menu.enabled_languages ?? menu.selected_languages ??
    menu.available_languages ?? menu.settings?.enabled_languages ??
    menu.settings?.selected_languages ?? menu.settings?.available_languages;

  if (raw === undefined || raw === null || raw === '') return SUPPORTED_LOCALES;

  let values = raw;
  if (typeof values === 'string') {
    try { values = JSON.parse(values); }
    catch { values = values.split(',').map((entry) => entry.trim()); }
  }
  if (values && !Array.isArray(values) && typeof values === 'object') {
    values = Object.keys(values).filter((key) => Boolean(values[key]));
  }
  if (!Array.isArray(values)) return SUPPORTED_LOCALES;

  const allowed = new Set(values.filter((item) => typeof item === 'string'));
  const defaultLocale = SUPPORTED_LOCALES.includes(menu.default_language)
    ? menu.default_language : 'fr';
  allowed.add(defaultLocale);
  return SUPPORTED_LOCALES.filter((locale) => allowed.has(locale));
};

const createEmptyTranslations = () =>
  Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [
      locale,
      { name: '', description: '', ingredients: '' },
    ]),
  );

const createEmptySizeTranslations = () =>
  Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [locale, { name: '' }]),
  );

const normalizeTranslations = (translations) =>
  Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [
      locale,
      {
        name: translations?.[locale]?.name ?? '',
        description: translations?.[locale]?.description ?? '',
        ingredients: translations?.[locale]?.ingredients ?? '',
      },
    ]),
  );

const normalizeSizeTranslations = (translations) =>
  Object.fromEntries(
    SUPPORTED_LOCALES.map((locale) => [
      locale,
      { name: translations?.[locale]?.name ?? '' },
    ]),
  );

const createEmptySize = (sortOrder = 1, isDefault = false) => ({
  price: '',
  discount_price: '',
  is_default: isDefault,
  is_active: true,
  sort_order: sortOrder,
  translations: createEmptySizeTranslations(),
});

const toBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }

  if (value === true || value === 1 || value === '1') {
    return true;
  }

  if (value === false || value === 0 || value === '0') {
    return false;
  }

  return Boolean(value);
};

const hasValue = (value) => {
  return value !== null && value !== undefined && String(value).trim() !== '';
};

const isVideoUrl = (url = '') => {
  const cleanUrl = String(url).split('?')[0].toLowerCase();

  return (
    cleanUrl.endsWith('.mp4') ||
    cleanUrl.endsWith('.webm') ||
    cleanUrl.endsWith('.mov') ||
    cleanUrl.endsWith('.m4v')
  );
};

const getMediaUrl = (media) => {
  return resolveMediaUrl(
    media?.url ||
      media?.image_url ||
      media?.media_url ||
      media?.video_url ||
      media?.path ||
      media?.image_path ||
      '',
  );
};

const isVideoMedia = (media) => {
  const mediaType =
    media?.type ||
    media?.mime_type ||
    media?.media_type ||
    media?.file_type ||
    '';

  if (String(mediaType).startsWith('video')) {
    return true;
  }

  return isVideoUrl(getMediaUrl(media));
};


function CustomSelect({
  value,
  options = [],
  onChange,
  disabled = false,
  placeholder = '—',
  ariaLabel,
  className = '',
  name,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );

    return () =>
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
  }, []);

  const selectedOption =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  return (
    <div
      ref={rootRef}
      className={`product-form-custom-select ${className}`}
    >
      {name ? (
        <input
          type="hidden"
          name={name}
          value={value}
        />
      ) : null}

      <button
        type="button"
        className={`product-form-custom-select__trigger ${
          open ? 'is-open' : ''
        }`}
        onClick={() => {
          if (!disabled) {
            setOpen(
              (current) =>
                !current,
            );
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        <span>
          {selectedOption?.label ||
            placeholder}
        </span>

        <ChevronDown
          size={17}
          className={
            open
              ? 'is-rotated'
              : ''
          }
        />
      </button>

      {open ? (
        <div
          className="product-form-custom-select__menu"
          role="listbox"
        >
          {options.map(
            (option) => {
              const active =
                String(
                  option.value,
                ) ===
                String(value);

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={
                    active
                      ? 'is-active'
                      : ''
                  }
                  onClick={() => {
                    onChange?.(
                      option.value,
                    );

                    setOpen(false);
                  }}
                >
                  <span>
                    {option.label}
                  </span>

                  {active ? (
                    <Check size={15} />
                  ) : null}
                </button>
              );
            },
          )}
        </div>
      ) : null}
    </div>
  );
}

function ProductForm() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const { confirm } = useConfirm();

  const isEditMode = Boolean(productId);
  const isRtl = language === 'ar';

  const [menus, setMenus] = useState([]);
  const [productMenuId, setProductMenuId] = useState('');
  const [categories, setCategories] = useState([]);
  const [availableSupplements, setAvailableSupplements] = useState([]);

  const [form, setForm] = useState({
    category_id: '',
    pricing_type: 'fixed',
    base_price: '',
    discount_price: '',
    status: 'available',
    is_featured: false,
    allow_supplements: true,
    sort_order: 0,
    regenerate_slug: false,
    translations: createEmptyTranslations(),
    images: [],
    sizes: [],
    supplements: [],
  });

  const [previewMedia, setPreviewMedia] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [localErrors, setLocalErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const establishmentId =
    localStorage.getItem('selected_establishment_id') ||
    DEFAULT_ESTABLISHMENT_ID;

  const pageTitle = isEditMode
    ? t('products.form.editTitle')
    : t('products.form.createTitle');

  const pageDescription = isEditMode
    ? t('products.form.editDescription')
    : t('products.form.createDescription');

  const selectedCategory = useMemo(() => {
    return categories.find(
      (category) => String(category.id) === String(form.category_id),
    );
  }, [categories, form.category_id]);

  const menuIdForLanguages = String(
    selectedCategory?.menu_id ?? selectedCategory?.menu?.id ??
    (productMenuId || localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || ''),
  );
  const selectedMenu = useMemo(() => {
    const storedMenuId = localStorage.getItem(SELECTED_MENU_STORAGE_KEY);
    return menus.find((menu) => String(menu.id) === menuIdForLanguages) ||
      menus.find((menu) => String(menu.id) === String(storedMenuId)) ||
      menus.find((menu) => Boolean(menu.is_default)) || menus[0] || null;
  }, [menus, menuIdForLanguages]);
  const visibleLocales = useMemo(() => getMenuLocales(selectedMenu), [selectedMenu]);

  const selectedCategoryName = useMemo(() => {
    return getTranslatedField(selectedCategory, 'name', language, 'fr');
  }, [selectedCategory, language]);

  const selectedCategoryParentName = useMemo(() => {
    return getTranslatedField(selectedCategory?.parent, 'name', language, 'fr');
  }, [selectedCategory, language]);

  const selectedSupplementIds = useMemo(() => {
    return new Set(
      form.supplements.map((supplement) =>
        String(supplement.product_supplement_id),
      ),
    );
  }, [form.supplements]);

  const clearLocalError = (field) => {
    setLocalErrors((currentErrors) => {
      if (!currentErrors[field]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      delete nextErrors[field];

      return nextErrors;
    });
  };

  const formatCategoryLabel = (category) => {
    const categoryName =
      getTranslatedField(category, 'name', language, 'fr') ||
      t('categories.noName');

    const parentName = getTranslatedField(
      category.parent,
      'name',
      language,
      'fr',
    );

    if (category.parent_id) {
      return `${parentName || t('categories.details.mainCategory')} / ${categoryName}`;
    }

    return categoryName;
  };

  const getSupplementName = (supplement) => {
    return getTranslatedField(supplement, 'name', language, 'fr');
  };

  const translateValidationMessage = (message) => {
    if (!message) {
      return '';
    }

    const normalizedMessage = String(message).toLowerCase();

    if (
      normalizedMessage.includes('true or false') ||
      normalizedMessage.includes('must be true') ||
      normalizedMessage.includes('boolean')
    ) {
      return t('validation.messages.boolean');
    }

    if (normalizedMessage.includes('required')) {
      return t('validation.messages.required');
    }

    if (normalizedMessage.includes('numeric')) {
      return t('validation.messages.numeric');
    }

    if (normalizedMessage.includes('integer')) {
      return t('validation.messages.integer');
    }

    if (normalizedMessage.includes('image')) {
      return t('validation.messages.image');
    }

    if (normalizedMessage.includes('mimes')) {
      return t('validation.messages.mimes');
    }

    if (normalizedMessage.includes('dimensions')) {
      return t('validation.messages.dimensions');
    }

    if (
      normalizedMessage.includes('less than') ||
      normalizedMessage.includes('must be less') ||
      normalizedMessage.includes('lower than')
    ) {
      return t('validation.messages.lessThanPrice');
    }

    if (normalizedMessage.includes('does not belong')) {
      return t('validation.messages.categoryOwnership');
    }

    return message;
  };

  const getFieldError = (field) => {
    if (localErrors[field]) {
      return localErrors[field];
    }

    const message = validationErrors[field]?.[0] || '';

    return translateValidationMessage(message);
  };

  const getErrorFieldSelector = (field) => {
    const escapedField =
      typeof CSS !== 'undefined' &&
      CSS.escape
        ? CSS.escape(field)
        : field.replace(
            /([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g,
            '\\$1',
          );

    return [
      `[name="${escapedField}"]`,
      `[data-error-field="${field}"]`,
    ];
  };

  const focusFirstError = (errors) => {
    const firstField =
      Object.keys(errors || {})[0];

    if (!firstField) {
      return;
    }

    window.setTimeout(() => {
      const selectors =
        getErrorFieldSelector(
          firstField,
        );

      let target = null;

      for (
        const selector
        of selectors
      ) {
        target =
          document.querySelector(
            selector,
          );

        if (target) {
          break;
        }
      }

      if (!target) {
        const baseField =
          firstField.split('.')[0];

        target =
          document.querySelector(
            `[name="${baseField}"]`,
          ) ||
          document.querySelector(
            `[data-error-field="${baseField}"]`,
          );
      }

      if (!target) {
        return;
      }

      const container =
        target.closest(
          '.product-form__field, .product-form-custom-select, .product-form__option-card, .product-form__translation-card',
        ) || target;

      container.classList.remove(
        'product-form__error-shake',
      );

      void container.offsetWidth;

      container.classList.add(
        'product-form__error-shake',
      );

      container.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      window.setTimeout(() => {
        const focusable =
          container.querySelector(
            'input:not([type="hidden"]), textarea, button, select',
          ) || target;

        focusable.focus?.({
          preventScroll: true,
        });
      }, 420);

      window.setTimeout(() => {
        container.classList.remove(
          'product-form__error-shake',
        );
      }, 760);
    }, 60);
  };

  const getImagesError = () => {
    if (localErrors.images) {
      return localErrors.images;
    }

    const directMessage = validationErrors.images?.[0];

    if (directMessage) {
      return translateValidationMessage(directMessage);
    }

    const imageErrorEntry = Object.entries(validationErrors).find(([field]) =>
      field.startsWith('images.'),
    );

    if (!imageErrorEntry) {
      return '';
    }

    const [, messages] = imageErrorEntry;
    const message = Array.isArray(messages) ? messages[0] : messages;

    return translateValidationMessage(message);
  };

  const fetchMenus = async () => {
    try {
      const response = await menusApi.getMenus(establishmentId);
      const records = response.data?.data?.menus ?? response.data?.data ?? [];
      setMenus(Array.isArray(records) ? records : []);
    } catch {
      // Do not hide editors if menu settings could not be fetched.
      setMenus([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response =
        await categoriesApi.getActiveCategories(establishmentId);

      setCategories(response.data.data || []);
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);

      setCategories([]);

      showAlert({
        type: 'warning',
        title: t('alerts.warning'),
        message: translatedError,
      });
    }
  };

  const fetchSupplements = async () => {
    try {
      const response = await productsApi.getActiveSupplements(establishmentId);

      setAvailableSupplements(response.data.data || []);
    } catch {
      setAvailableSupplements([]);
    }
  };

  const fetchProduct = async () => {
    if (!isEditMode) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await productsApi.getProduct(productId);
      const product = response.data.data;
      setProductMenuId(String(product.menu_id ?? product.menu?.id ?? ''));


      setForm({
        category_id: product.category_id || '',
        pricing_type: product.pricing_type || 'fixed',
        base_price: product.base_price || '',
        discount_price: product.discount_price || '',
        status: product.status || 'available',
        is_featured: toBoolean(product.is_featured),
        allow_supplements: toBoolean(product.allow_supplements, true),
        sort_order: product.sort_order || 0,
        regenerate_slug: false,
        translations: normalizeTranslations(product.translations),
        images: [],
        sizes: (product.sizes || []).map((size, index) => ({
          price: size.price || '',
          discount_price: size.discount_price || '',
          is_default: toBoolean(size.is_default),
          is_active: toBoolean(size.is_active, true),
          sort_order: size.sort_order ?? index + 1,
          translations: normalizeSizeTranslations(size.translations),
        })),
        supplements: (product.supplements || []).map((supplement, index) => ({
          product_supplement_id: supplement.id,
          price_override: supplement.price_override ?? '',
          is_default: toBoolean(supplement.is_default),
          is_available: toBoolean(supplement.is_available, true),
          sort_order: supplement.sort_order ?? index + 1,
        })),
      });

      setExistingImages(product.images || []);
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplements();

    fetchCategories();
    fetchMenus();

    if (isEditMode) {
      fetchProduct();
    }
  }, [productId]);

  useEffect(() => {
    return () => {
      previewMedia.forEach((media) => {
        URL.revokeObjectURL(media.url);
      });
    };
  }, [previewMedia]);

  const updateField = (field, value) => {
    clearLocalError(field);

    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const updateTranslation = (locale, field, value) => {
    clearLocalError('translations');
    clearLocalError(`translations.${locale}.${field}`);

    setForm((currentForm) => ({
      ...currentForm,
      translations: {
        ...currentForm.translations,
        [locale]: {
          ...currentForm.translations?.[locale],
          [field]: value,
        },
      },
    }));
  };

  const handlePricingTypeChange = (pricingType) => {
    clearLocalError('base_price');
    clearLocalError('discount_price');
    clearLocalError('sizes');

    setForm((currentForm) => ({
      ...currentForm,
      pricing_type: pricingType,
      sizes:
        pricingType === 'sizes' && currentForm.sizes.length === 0
          ? [createEmptySize(1, true)]
          : currentForm.sizes,
    }));
  };

  const addSize = () => {
    clearLocalError('sizes');

    setForm((currentForm) => ({
      ...currentForm,
      sizes: [
        ...currentForm.sizes,
        createEmptySize(
          currentForm.sizes.length + 1,
          currentForm.sizes.length === 0,
        ),
      ],
    }));
  };

  const updateSize = (index, field, value) => {
    clearLocalError(`sizes.${index}.${field}`);
    clearLocalError(`sizes.${index}.discount_price`);

    setForm((currentForm) => {
      const sizes = currentForm.sizes.map((size, sizeIndex) => {
        if (field === 'is_default') {
          return {
            ...size,
            is_default: sizeIndex === index ? value : false,
          };
        }

        if (sizeIndex !== index) {
          return size;
        }

        return {
          ...size,
          [field]: value,
        };
      });

      return {
        ...currentForm,
        sizes,
      };
    });
  };

  const updateSizeTranslation = (index, locale, value) => {
    clearLocalError(`sizes.${index}.translations`);
    clearLocalError(`sizes.${index}.translations.${locale}.name`);

    setForm((currentForm) => ({
      ...currentForm,
      sizes: currentForm.sizes.map((size, sizeIndex) => {
        if (sizeIndex !== index) {
          return size;
        }

        return {
          ...size,
          translations: {
            ...size.translations,
            [locale]: {
              ...size.translations?.[locale],
              name: value,
            },
          },
        };
      }),
    }));
  };

  const removeSize = async (index) => {
    const confirmed = await confirm({
      title: t('products.actions.delete'),
      message: t('products.form.confirmDeleteSize'),
      itemName: `${t('products.form.size')} #${index + 1}`,
      confirmText: t('products.actions.delete'),
      cancelText: t('common.cancel'),
      closeText: t('common.close'),
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    setForm((currentForm) => {
      const sizes = currentForm.sizes.filter(
        (_, sizeIndex) => sizeIndex !== index,
      );

      if (
        sizes.length > 0 &&
        !sizes.some((size) => size.is_default)
      ) {
        sizes[0] = {
          ...sizes[0],
          is_default: true,
        };
      }

      return {
        ...currentForm,
        sizes,
      };
    });
  };

  const getSupplementSelection = (supplementId) => {
    return form.supplements.find(
      (supplement) =>
        String(supplement.product_supplement_id) === String(supplementId),
    );
  };

  const toggleSupplement = (supplementId) => {
    setForm((currentForm) => {
      const exists = currentForm.supplements.some(
        (supplement) =>
          String(supplement.product_supplement_id) === String(supplementId),
      );

      if (exists) {
        return {
          ...currentForm,
          supplements: currentForm.supplements.filter(
            (supplement) =>
              String(supplement.product_supplement_id) !== String(supplementId),
          ),
        };
      }

      return {
        ...currentForm,
        supplements: [
          ...currentForm.supplements,
          {
            product_supplement_id: supplementId,
            price_override: '',
            is_default: false,
            is_available: true,
            sort_order: currentForm.supplements.length + 1,
          },
        ],
      };
    });
  };

  const updateSupplementField = (supplementId, field, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      supplements: currentForm.supplements.map((supplement) => {
        if (String(supplement.product_supplement_id) !== String(supplementId)) {
          return supplement;
        }

        return {
          ...supplement,
          [field]: value,
        };
      }),
    }));
  };

  const validateMediaFiles = (files) => {
    if (files.length > MAX_MEDIA_FILES) {
      return t('products.form.mediaMaxFiles')
        .replace('{{count}}', String(MAX_MEDIA_FILES));
    }

    const invalidFile = files.find(
      (file) => !isAcceptedMediaFile(file),
    );

    if (invalidFile) {
      return t('products.form.mediaTypes');
    }

    const oversizedFile = files.find((file) => {
      const maxSizeMb = file.type.startsWith('video/')
        ? MAX_VIDEO_SIZE_MB
        : MAX_IMAGE_SIZE_MB;

      return file.size > maxSizeMb * 1024 * 1024;
    });

    if (oversizedFile) {
      return t('products.form.mediaSizeLimits')
        .replace('{{imageSize}}', String(MAX_IMAGE_SIZE_MB))
        .replace('{{videoSize}}', String(MAX_VIDEO_SIZE_MB));
    }

    return '';
  };

  const handleImagesChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    const nextFiles = [
      ...form.images,
      ...files,
    ];

    const mediaError = validateMediaFiles(nextFiles);

    if (mediaError) {
      setLocalErrors((currentErrors) => ({
        ...currentErrors,
        images: mediaError,
      }));

      event.target.value = '';

      return;
    }

    setLocalErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors.images;

      return nextErrors;
    });

    const newPreviewMedia = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID?.() || Math.random()}`,
      name: file.name,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      file,
      url: URL.createObjectURL(file),
    }));

    setForm((currentForm) => ({
      ...currentForm,
      images: [
        ...currentForm.images,
        ...files,
      ],
    }));

    setPreviewMedia((currentMedia) => [
      ...currentMedia,
      ...newPreviewMedia,
    ]);

    event.target.value = '';
  };

  const handleDeletePreviewMedia = async (mediaId) => {
    const mediaToDelete = previewMedia.find(
      (media) => media.id === mediaId,
    );

    const confirmed = await confirm({
      title: t('products.actions.delete'),
      message: t('products.form.confirmDeleteImage'),
      itemName: mediaToDelete?.name || '',
      confirmText: t('products.actions.delete'),
      cancelText: t('common.cancel'),
      closeText: t('common.close'),
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    setPreviewMedia((currentMedia) => {
      const mediaToRemove = currentMedia.find(
        (media) => media.id === mediaId,
      );

      if (mediaToRemove?.url) {
        URL.revokeObjectURL(mediaToRemove.url);
      }

      if (mediaToRemove?.file) {
        setForm((currentForm) => ({
          ...currentForm,
          images: currentForm.images.filter(
            (file) => file !== mediaToRemove.file,
          ),
        }));
      }

      return currentMedia.filter(
        (media) => media.id !== mediaId,
      );
    });

    clearLocalError('images');
  };

  const handleDeleteExistingImage = async (imageId) => {
    const confirmed = await confirm({
      title: t('products.actions.delete'),
      message: t('products.form.confirmDeleteImage'),
      itemName: '',
      confirmText: t('products.actions.delete'),
      cancelText: t('common.cancel'),
      closeText: t('common.close'),
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      await productsApi.deleteProductImage(imageId);

      setExistingImages((currentImages) =>
        currentImages.filter((image) => image.id !== imageId),
      );

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t('products.alerts.imageDeleted'),
      });
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);

      setError(translatedError);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    }
  };

  const normalizeOptionalNumber = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return value;
  };

  const normalizeBoolean = (value) => {
    return value ? 1 : 0;
  };

  const normalizeSizes = () => {
    return form.sizes.map((size, index) => ({
      price: size.price,
      discount_price: normalizeOptionalNumber(size.discount_price),
      is_default: normalizeBoolean(size.is_default),
      is_active: normalizeBoolean(size.is_active),
      sort_order: size.sort_order || index + 1,
      translations: size.translations,
    }));
  };

  const normalizeSupplements = () => {
    return form.supplements.map((supplement, index) => ({
      product_supplement_id: supplement.product_supplement_id,
      price_override: normalizeOptionalNumber(supplement.price_override),
      is_default: normalizeBoolean(supplement.is_default),
      is_available: normalizeBoolean(supplement.is_available),
      sort_order: supplement.sort_order || index + 1,
    }));
  };

  const hasAtLeastOneProductName = () => {
    return Object.values(form.translations).some((translation) =>
      hasValue(translation?.name),
    );
  };

  const hasAtLeastOneSizeName = (translations) => {
    return Object.values(translations || {}).some((translation) =>
      hasValue(translation?.name),
    );
  };

  const validateForm = () => {
    const errors = {};

    if (!hasValue(form.category_id)) {
      errors.category_id = t('validation.messages.required');
    }

    if (!hasAtLeastOneProductName()) {
      errors.translations = t('validation.messages.required');
    }

    if (form.pricing_type === 'fixed') {
      if (!hasValue(form.base_price)) {
        errors.base_price = t('validation.messages.required');
      }

      if (
        hasValue(form.base_price) &&
        hasValue(form.discount_price) &&
        Number(form.discount_price) >= Number(form.base_price)
      ) {
        errors.discount_price = t('validation.messages.lessThanPrice');
      }
    }

    if (form.pricing_type === 'sizes') {
      if (form.sizes.length === 0) {
        errors.sizes = t('validation.messages.required');
      }

      form.sizes.forEach((size, index) => {
        if (!hasValue(size.price)) {
          errors[`sizes.${index}.price`] = t('validation.messages.required');
        }

        if (!hasAtLeastOneSizeName(size.translations)) {
          errors[`sizes.${index}.translations`] =
            t('validation.messages.required');
        }

        if (
          hasValue(size.price) &&
          hasValue(size.discount_price) &&
          Number(size.discount_price) >= Number(size.price)
        ) {
          errors[`sizes.${index}.discount_price`] =
            t('validation.messages.lessThanPrice');
        }
      });
    }

    const mediaError = validateMediaFiles(form.images);

    if (mediaError) {
      errors.images = mediaError;
    }

    return errors;
  };

  const buildPayload = () => {
    const isSizePricing = form.pricing_type === 'sizes';

    return {
      category_id: Number(form.category_id),
      pricing_type: form.pricing_type,

      base_price: isSizePricing ? null : form.base_price,
      discount_price: isSizePricing
        ? null
        : normalizeOptionalNumber(form.discount_price),

      status: form.status,
      is_featured: normalizeBoolean(form.is_featured),
      allow_supplements: normalizeBoolean(form.allow_supplements),
      sort_order: form.sort_order || 0,
      regenerate_slug: normalizeBoolean(form.regenerate_slug),

      translations: form.translations,
      images: form.images,

      sizes: isSizePricing ? normalizeSizes() : [],
      supplements: form.allow_supplements ? normalizeSupplements() : [],
    };
  };

  const renderMediaPreview = (media, altText = t('products.form.name')) => {
    const mediaUrl = getMediaUrl(media);
    const video = media.type === 'video' || isVideoMedia(media);

    if (!mediaUrl) {
      return (
        <div className="product-form__media-placeholder">
          <ImagePlus size={24} />
        </div>
      );
    }

    if (video) {
      return (
        <video src={mediaUrl} controls muted playsInline>
          <track kind="captions" />
        </video>
      );
    }

    return <img src={mediaUrl} alt={altText} />;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const frontendErrors = validateForm();

    if (Object.keys(frontendErrors).length > 0) {
      setLocalErrors(frontendErrors);
      setValidationErrors({});

      focusFirstError(frontendErrors);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: t('validation.messages.required'),
      });

      return;
    }

    try {
      setSaving(true);
      setError('');
      setLocalErrors({});
      setValidationErrors({});

      const payload = buildPayload();

      if (isEditMode) {
        await productsApi.updateProduct(productId, payload);

        showAlert({
          type: 'success',
          title: t('alerts.success'),
          message: t('products.alerts.updated'),
        });
      } else {
        await productsApi.createProduct(establishmentId, payload);

        showAlert({
          type: 'success',
          title: t('alerts.success'),
          message: t('products.alerts.created'),
        });
      }

      navigate('/products');
    } catch (apiError) {
      const translatedError = translateApiError(apiError, t);

      setError(translatedError);

      const apiErrors =
        getApiValidationErrors(
          apiError,
        );

      setValidationErrors(
        apiErrors,
      );

      focusFirstError(
        apiErrors,
      );

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translatedError,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main
        className={`product-form-page ${
          isRtl
            ? 'product-form-page--rtl'
            : 'product-form-page--ltr'
        }`}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <section className="product-form-loading-panel">
          <div className="product-form-loading">
            <Loader2 size={28} />
            <span>{t('products.loading')}</span>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      className={`product-form-page ${
        isRtl
          ? 'product-form-page--rtl'
          : 'product-form-page--ltr'
      }`}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <header className="product-form-header">
        <button
          type="button"
          className="product-form-back-button"
          onClick={() => navigate('/products')}
        >
          {isRtl ? (
            <ArrowRight size={18} />
          ) : (
            <ArrowLeft size={18} />
          )}

          <span>{t('common.back')}</span>
        </button>

        <div className="product-form-header__copy">
          <p className="product-form-eyebrow">
            {t('products.eyebrow')}
          </p>

          <h1>{pageTitle}</h1>

          <span>{pageDescription}</span>
        </div>
      </header>

      {error && (
        <div
          className="product-form-alert product-form-alert--error"
          role="alert"
        >
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}


      <form className="product-form" onSubmit={handleSubmit}>
        <section className="product-form-section product-form-section--basic">
          <div className="product-form-section__title">
            <span className="product-form-section__icon">
              <ListPlus size={20} />
            </span>

            <div>
              <h2>{t('products.form.basicInfo')}</h2>
              <p>{t('products.form.basicInfoDescription')}</p>
            </div>
          </div>

          <div className="product-form__grid">
            <label className="product-form__field">
              <span>{t('products.form.category')}</span>
              <CustomSelect
                name="category_id"
                value={form.category_id}
                className={
                  getFieldError('category_id')
                    ? 'has-error'
                    : ''
                }
                onChange={(value) =>
                  updateField(
                    'category_id',
                    value,
                  )
                }
                ariaLabel={t(
                  'products.form.category',
                )}
                placeholder={t(
                  'products.form.noCategory',
                )}
                options={[
                  {
                    value: '',
                    label: t(
                      'products.form.noCategory',
                    ),
                  },

                  ...categories.map(
                    (category) => ({
                      value:
                        String(
                          category.id,
                        ),
                      label:
                        formatCategoryLabel(
                          category,
                        ),
                    }),
                  ),
                ]}
              />

              {selectedCategoryName && (
                <small>
                  {selectedCategoryParentName
                    ? `${selectedCategoryParentName} / ${selectedCategoryName}`
                    : selectedCategoryName}
                </small>
              )}

              {getFieldError('category_id') && (
                <em>{getFieldError('category_id')}</em>
              )}
            </label>

            <label className="product-form__field">
              <span>{t('products.form.pricingType')}</span>
              <CustomSelect
                name="pricing_type"
                value={form.pricing_type}
                className={
                  getFieldError('pricing_type')
                    ? 'has-error'
                    : ''
                }
                onChange={
                  handlePricingTypeChange
                }
                ariaLabel={t(
                  'products.form.pricingType',
                )}
                options={[
                  {
                    value: 'fixed',
                    label: t(
                      'products.pricing.fixed',
                    ),
                  },
                  {
                    value: 'sizes',
                    label: t(
                      'products.pricing.sizes',
                    ),
                  },
                ]}
              />

              {getFieldError('pricing_type') && (
                <em>{getFieldError('pricing_type')}</em>
              )}
            </label>

            {form.pricing_type === 'fixed' && (
              <>
                <label className="product-form__field">
                  <span>{t('products.form.basePrice')}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.base_price}
                    onChange={(event) =>
                      updateField('base_price', event.target.value)
                    }
                  />

                  {getFieldError('base_price') && (
                    <em>{getFieldError('base_price')}</em>
                  )}
                </label>

                <label className="product-form__field">
                  <span>{t('products.form.discountPrice')}</span>
                  <input
                    name="discount_price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.discount_price}
                    onChange={(event) =>
                      updateField('discount_price', event.target.value)
                    }
                  />

                  {getFieldError('discount_price') && (
                    <em>{getFieldError('discount_price')}</em>
                  )}
                </label>
              </>
            )}

            <label className="product-form__field">
              <span>{t('products.form.status')}</span>
              <CustomSelect
                name="status"
                value={form.status}
                className={
                  getFieldError('status')
                    ? 'has-error'
                    : ''
                }
                onChange={(value) =>
                  updateField(
                    'status',
                    value,
                  )
                }
                ariaLabel={t(
                  'products.form.status',
                )}
                options={[
                  {
                    value:
                      'available',
                    label: t(
                      'products.status.available',
                    ),
                  },
                  {
                    value:
                      'sold_out',
                    label: t(
                      'products.status.sold_out',
                    ),
                  },
                  {
                    value:
                      'hidden',
                    label: t(
                      'products.status.hidden',
                    ),
                  },
                ]}
              />

              {getFieldError('status') && (
                <em>{getFieldError('status')}</em>
              )}
            </label>

            <label className="product-form__field">
              <span>{t('products.form.sortOrder')}</span>
              <input
                name="sort_order"
                type="number"
                min="0"
                value={form.sort_order}
                onChange={(event) =>
                  updateField('sort_order', event.target.value)
                }
              />

              {getFieldError('sort_order') && (
                <em>{getFieldError('sort_order')}</em>
              )}
            </label>

            <label className="product-form__checkbox">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(event) =>
                  updateField('is_featured', event.target.checked)
                }
              />
              <span>{t('products.form.isFeatured')}</span>
            </label>

            <label className="product-form__checkbox">
              <input
                type="checkbox"
                checked={form.allow_supplements}
                onChange={(event) =>
                  updateField('allow_supplements', event.target.checked)
                }
              />
              <span>{t('products.form.allowSupplements')}</span>
            </label>

            {isEditMode && (
              <label className="product-form__checkbox">
                <input
                  type="checkbox"
                  checked={form.regenerate_slug}
                  onChange={(event) =>
                    updateField('regenerate_slug', event.target.checked)
                  }
                />
                <span>{t('products.form.regenerateSlug')}</span>
              </label>
            )}
          </div>
        </section>

        {form.pricing_type === 'sizes' && (
          <section className="product-form-section">
            <div className="product-form-section__title">
              <span className="product-form-section__icon">
                <Ruler size={20} />
              </span>

              <div>
                <h2>{t('products.form.sizes')}</h2>
                <p>{t('products.form.sizesDescription')}</p>
              </div>
            </div>

            {getFieldError('sizes') && (
              <div className="product-form-alert product-form-alert--error">
                {getFieldError('sizes')}
              </div>
            )}

            <div className="product-form__options-list">
              {form.sizes.map((size, index) => (
                <article className="product-form__option-card" key={index}>
                  <div className="product-form__option-header">
                    <span>
                      <Ruler size={18} />
                      {t('products.form.size')} #{index + 1}
                    </span>

                    <button
                      type="button"
                      onClick={() => removeSize(index)}
                      className="product-form__icon-button product-form__icon-button--danger"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="product-form__grid">
                    <label className="product-form__field">
                      <span>{t('products.form.sizePrice')}</span>
                      <input
                        name={`sizes.${index}.price`}
                        data-error-field={`sizes.${index}.price`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={size.price}
                        onChange={(event) =>
                          updateSize(index, 'price', event.target.value)
                        }
                      />

                      {getFieldError(`sizes.${index}.price`) && (
                        <em>{getFieldError(`sizes.${index}.price`)}</em>
                      )}
                    </label>

                    <label className="product-form__field">
                      <span>{t('products.form.sizeDiscountPrice')}</span>
                      <input
                        name={`sizes.${index}.discount_price`}
                        data-error-field={`sizes.${index}.discount_price`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={size.discount_price}
                        onChange={(event) =>
                          updateSize(
                            index,
                            'discount_price',
                            event.target.value,
                          )
                        }
                      />

                      {getFieldError(`sizes.${index}.discount_price`) && (
                        <em>
                          {getFieldError(`sizes.${index}.discount_price`)}
                        </em>
                      )}
                    </label>

                    <label className="product-form__field">
                      <span>{t('products.form.sortOrder')}</span>
                      <input
                        name={`sizes.${index}.sort_order`}
                        type="number"
                        min="0"
                        value={size.sort_order}
                        onChange={(event) =>
                          updateSize(index, 'sort_order', event.target.value)
                        }
                      />
                    </label>

                    <label className="product-form__checkbox">
                      <input
                        type="checkbox"
                        checked={size.is_default}
                        onChange={(event) =>
                          updateSize(index, 'is_default', event.target.checked)
                        }
                      />
                      <span>{t('products.form.defaultSize')}</span>
                    </label>

                    <label className="product-form__checkbox">
                      <input
                        type="checkbox"
                        checked={size.is_active}
                        onChange={(event) =>
                          updateSize(index, 'is_active', event.target.checked)
                        }
                      />
                      <span>{t('products.form.activeSize')}</span>
                    </label>
                  </div>

                  <div className="product-form__mini-translations">
                    {visibleLocales.map((locale) => (
                      <label className="product-form__field" key={locale}>
                        <span>
                          {t('products.form.sizeName')} -{' '}
                          {t(`languages.${locale}`)}
                        </span>
                        <input
                          name={`sizes.${index}.translations.${locale}.name`}
                          data-error-field={`sizes.${index}.translations.${locale}.name`}
                          type="text"
                          value={size.translations?.[locale]?.name ?? ''}
                          onChange={(event) =>
                            updateSizeTranslation(
                              index,
                              locale,
                              event.target.value,
                            )
                          }
                          dir={locale === 'ar' ? 'rtl' : 'ltr'}
                        />

                        {getFieldError(
                          `sizes.${index}.translations.${locale}.name`,
                        ) && (
                          <em>
                            {getFieldError(
                              `sizes.${index}.translations.${locale}.name`,
                            )}
                          </em>
                        )}
                      </label>
                    ))}
                  </div>

                  {getFieldError(`sizes.${index}.translations`) && (
                    <div className="product-form-alert product-form-alert--error">
                      {getFieldError(`sizes.${index}.translations`)}
                    </div>
                  )}
                </article>
              ))}
            </div>

            <button
              type="button"
              className="dashboard-secondary-button"
              onClick={addSize}
            >
              <Plus size={18} />
              {t('products.form.addSize')}
            </button>
          </section>
        )}

        <section className="product-form-section">
          <div className="product-form-section__title">
            <span className="product-form-section__icon">
              <Languages size={20} />
            </span>

            <div>
              <h2>{t('products.form.translations')}</h2>
              <p>{t('products.form.translationsDescription')}</p>
            </div>
          </div>

          <div className="product-form__translations">
            {visibleLocales.map((locale) => (
              <div className="product-form__translation-card" key={locale}>
                <h3>{t(`languages.${locale}`)}</h3>

                <label className="product-form__field">
                  <span>{t('products.form.name')}</span>
                  <input
                    name={`translations.${locale}.name`}
                    data-error-field={`translations.${locale}.name`}
                    type="text"
                    value={form.translations?.[locale]?.name ?? ''}
                    onChange={(event) =>
                      updateTranslation(locale, 'name', event.target.value)
                    }
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />

                  {getFieldError(`translations.${locale}.name`) && (
                    <em>{getFieldError(`translations.${locale}.name`)}</em>
                  )}
                </label>

                <label className="product-form__field">
                  <span>{t('products.form.description')}</span>
                  <textarea
                    name={`translations.${locale}.description`}
                    data-error-field={`translations.${locale}.description`}
                    value={form.translations?.[locale]?.description ?? ''}
                    onChange={(event) =>
                      updateTranslation(
                        locale,
                        'description',
                        event.target.value,
                      )
                    }
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />

                  {getFieldError(`translations.${locale}.description`) && (
                    <em>
                      {getFieldError(`translations.${locale}.description`)}
                    </em>
                  )}
                </label>

                <label className="product-form__field">
                  <span>{t('products.form.ingredients')}</span>
                  <textarea
                    name={`translations.${locale}.ingredients`}
                    data-error-field={`translations.${locale}.ingredients`}
                    value={form.translations?.[locale]?.ingredients ?? ''}
                    onChange={(event) =>
                      updateTranslation(
                        locale,
                        'ingredients',
                        event.target.value,
                      )
                    }
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  />

                  {getFieldError(`translations.${locale}.ingredients`) && (
                    <em>
                      {getFieldError(`translations.${locale}.ingredients`)}
                    </em>
                  )}
                </label>
              </div>
            ))}
          </div>

          {getFieldError('translations') && (
            <div className="product-form-alert product-form-alert--error">
              {getFieldError('translations')}
            </div>
          )}
        </section>

        {form.allow_supplements && (
          <section className="product-form-section">
            <div className="product-form-section__title">
              <span className="product-form-section__icon">
                <ListPlus size={20} />
              </span>

              <div>
                <h2>{t('products.form.supplements')}</h2>
                <p>{t('products.form.supplementsDescription')}</p>
              </div>
            </div>

            {availableSupplements.length > 0 ? (
              <div className="product-form__supplements-grid">
                {availableSupplements.map((supplement) => {
                  const isSelected = selectedSupplementIds.has(
                    String(supplement.id),
                  );

                  const selectedSupplement = getSupplementSelection(
                    supplement.id,
                  );

                  return (
                    <article
                      className={`product-form__supplement-card ${
                        isSelected ? 'product-form__supplement-card--active' : ''
                      }`}
                      key={supplement.id}
                    >
                      <label className="product-form__supplement-main">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSupplement(supplement.id)}
                        />

                        <span>
                          <ListPlus size={18} />
                        </span>

                        <div>
                          <strong>
                            {getSupplementName(supplement) ||
                              t('products.form.supplement')}
                          </strong>
                          <small>
                            {t('products.form.supplementBasePrice')}:{' '}
                            {supplement.price} MAD
                          </small>
                        </div>
                      </label>

                      {isSelected && (
                        <div className="product-form__supplement-options">
                          <label className="product-form__field">
                            <span>{t('products.form.priceOverride')}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={selectedSupplement?.price_override || ''}
                              onChange={(event) =>
                                updateSupplementField(
                                  supplement.id,
                                  'price_override',
                                  event.target.value,
                                )
                              }
                            />
                          </label>

                          <label className="product-form__checkbox">
                            <input
                              type="checkbox"
                              checked={Boolean(
                                selectedSupplement?.is_default,
                              )}
                              onChange={(event) =>
                                updateSupplementField(
                                  supplement.id,
                                  'is_default',
                                  event.target.checked,
                                )
                              }
                            />
                            <span>{t('products.form.defaultSupplement')}</span>
                          </label>

                          <label className="product-form__checkbox">
                            <input
                              type="checkbox"
                              checked={
                                selectedSupplement?.is_available !== false
                              }
                              onChange={(event) =>
                                updateSupplementField(
                                  supplement.id,
                                  'is_available',
                                  event.target.checked,
                                )
                              }
                            />
                            <span>
                              {t('products.form.availableSupplement')}
                            </span>
                          </label>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="products-empty-state">
                <ListPlus size={34} />
                <h2>{t('products.form.noSupplements')}</h2>
              </div>
            )}
          </section>
        )}

        <section className="product-form-section">
          <div className="product-form-section__title">
            <span className="product-form-section__icon">
              <ImagePlus size={20} />
            </span>

            <div>
              <h2>{t('products.form.images')}</h2>
              <p>{t('products.form.imagesDescription')}</p>
            </div>
          </div>

          <label className="product-form__upload">
            <ImagePlus size={28} />
            <strong>{t('products.form.uploadImages')}</strong>
            <span>
              {t('products.form.uploadMediaHelp')
                .replace('{{count}}', String(MAX_MEDIA_FILES))
                .replace('{{imageSize}}', String(MAX_IMAGE_SIZE_MB))
                .replace('{{videoSize}}', String(MAX_VIDEO_SIZE_MB))}
            </span>
            <input
              name="images"
              data-error-field="images"
              type="file"
              multiple
              accept="image/*,video/mp4,video/webm,video/quicktime,video/x-m4v"
              onChange={handleImagesChange}
            />
          </label>

          {getImagesError() && (
            <div className="product-form-alert product-form-alert--error">
              {getImagesError()}
            </div>
          )}

          {(existingImages.length > 0 || previewMedia.length > 0) && (
            <div className="product-form__images-grid">
              {existingImages.map((media) => (
                <div className="product-form__image-preview" key={media.id}>
                  {renderMediaPreview(media, media.alt_text || t('products.form.name'))}

                  <button
                    type="button"
                    onClick={() => handleDeleteExistingImage(media.id)}
                  >
                    {t('products.actions.delete')}
                  </button>
                </div>
              ))}

              {previewMedia.map((media) => (
                <div className="product-form__image-preview" key={media.id}>
                  {renderMediaPreview(media, media.name)}

                  <button
                    type="button"
                    onClick={() => handleDeletePreviewMedia(media.id)}
                  >
                    <Trash2 size={15} />
                    {t('products.actions.delete')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="product-form__actions">
          <button
            type="button"
            className="product-form-cancel-button"
            onClick={() => navigate('/products')}
          >
            {t('common.cancel')}
          </button>

          <button
            type="submit"
            className="product-form-save-button"
            disabled={saving}
          >
            {saving ? <Loader2 size={18} /> : <Save size={18} />}

            <span>
              {isEditMode
                ? t('products.form.update')
                : t('products.form.save')}
            </span>
          </button>
        </div>
      </form>
    </main>
  );
}

export default ProductForm;